/**
 * The audit log's decisions, as functions — why these are not JSX.
 *
 * The standing rule, sharpened at c35: *"state set only by an event is state a static
 * render cannot produce, so anything it gates needs a component of its own"*, and
 * *"extract the decision"* is the same rule at the level that is easy to satisfy without
 * fixing anything. This file is the decisions; `AuditLog.tsx` exports the renderings that
 * sit behind the one interaction this screen has (expanding a row's diff), so both halves
 * are reachable without a reconciler.
 */
import { isAuditAction, type AuditActor } from "@packagepage/shared";

/**
 * One word for who did it.
 *
 * A ladder rather than a single field, because all four of `actor`'s members are
 * independently nullable and each absence means something different:
 *
 *   - a **name** if the person set one — `users.name` is nullable
 *   - otherwise their **email**, which `users.email` guarantees
 *   - otherwise **"a deleted account"**, because `audit_log.actor_user_id` is
 *     `ON DELETE SET NULL`: the row outlives the person, which is what append-only means.
 *     The row is still evidence of what happened; only the actor is gone.
 *   - otherwise **"an API key"** where a machine credential acted
 *   - otherwise **"the system"**, for a row with neither actor — which nothing writes
 *     today and which the schema permits, so the screen answers rather than renders
 *     `null`.
 *
 * Deliberately never *"unknown"*: every rung above is a different fact and collapsing them
 * would throw away the one a reader is asking about.
 */
export function actorLabel(actor: AuditActor): string {
  if (actor.name !== null && actor.name.trim() !== "") return actor.name;
  if (actor.email !== null && actor.email.trim() !== "") return actor.email;
  if (actor.userId !== null) return "a deleted account";
  if (actor.apiKeyId !== null) return "an API key";
  return "the system";
}

/**
 * What the action token is, and whether this build knows it.
 *
 * **The token is rendered as written, and that is a decision rather than a shortcut.**
 * `AUDIT_ACTIONS`' own header says these strings are product copy — *"read by customers,
 * in their own dashboard"*, and renaming one changes what an already-written row appears
 * to say. Giving each a second English phrase here would be a **second vocabulary that can
 * drift from the first**, which is c22's invented-Surface-vocabulary finding and c35's
 * three-spellings-of-one-ordering finding arriving together. `resource.verb` is already
 * readable, and it is the same string a support conversation will quote.
 *
 * `known` exists because the wire schema is deliberately lenient (see
 * `auditLogEntrySchema`): a dashboard running the previous bundle during a deploy will
 * meet actions it has not heard of, and showing one plainly is right where failing the
 * page is not.
 */
export interface ActionDescription {
  /** The exact string the write path recorded. Kept for support conversations. */
  readonly token: string;
  readonly known: boolean;
  /** What the WHAT column reads. Falls back to the token when no phrase exists. */
  readonly phrase: string;
}

/**
 * The plain-English phrase for each action — RULED by `screens.md` §13 (2026-08-11).
 *
 * *"`WHAT` (plain-English verb phrase, not an event code)."*
 *
 * **This reverses a decision recorded in this file.** The token was rendered as written,
 * on the argument that an audit trail is evidence and evidence should not be paraphrased.
 * The ruling wins, and its reasoning is that the log's reader is a customer or an auditor
 * rather than an engineer — `link.deactivate` is not evidence to them, it is a string
 * they have to translate before they can judge it.
 *
 * The token is not lost: it stays on the row as the `title` and in mono, so a support
 * conversation can still name the exact string. The phrase is what the column reads.
 *
 * Keyed on `AUDIT_ACTIONS` deliberately — an action with no phrase falls back to the
 * token rather than to a guess, and `known: false` still marks it, so a new action added
 * upstream shows up as an unphrased token rather than as a plausible sentence somebody
 * invented.
 */
const ACTION_PHRASES: Readonly<Record<string, string>> = {
  "member.invite": "invited a member",
  "member.remove": "removed a member",
  "member.role_change": "changed a member's role",
  "invite.revoke": "withdrew an invitation",
  "ownership.transfer": "handed over ownership",
  "apiKey.create": "created an API key",
  "apiKey.revoke": "revoked an API key",
  "org.create": "created the organisation",
  "org.update": "updated the organisation",
  "workspace.create": "created a workspace",
  "workspace.update": "updated a workspace",
  "workspace.delete": "deleted a workspace",
  "product.create": "added a product",
  "product.update": "updated a product",
  "product.publish": "published a product",
  "product.retire": "retired a product",
  "link.create": "added a link",
  "link.update": "updated a link",
  "link.deactivate": "deactivated a link",
  "link.activate": "reactivated a link",
  "link.delete": "deleted a link",
  "link.reorder": "reordered links",
  // Recall wording follows the flow's own vocabulary — "publish", "widen", "lift" — and
  // never "stand down", which is a second spelling of "lift" the handoff used once and
  // which c35 recorded as the class of drift to refuse.
  "recall.publish": "published a recall notice",
  "recall.widen": "widened a recall",
  "recall.lift": "lifted a recall",
  // `prefix.verify` and `prefix.reject` carry a NULL actor, so the row reads "the system
  // verified a company prefix" — Decision 2026-09-23 §3.
  "prefix.claim": "claimed a company prefix",
  "prefix.evidence": "sent a GS1 licence certificate",
  "prefix.verify": "verified a company prefix",
  "prefix.reject": "declined a company prefix's certificate",
  // Written from a Stripe event with a NULL actor, so the row reads "the system changed…".
  "subscription.changed": "changed the subscription",
  "import.create": "started a spreadsheet import",
  "import.cancel": "cancelled a spreadsheet import",
  "import.apply": "applied a spreadsheet import",
};

export function describeAction(action: string): ActionDescription {
  return {
    token: action,
    known: isAuditAction(action),
    phrase: ACTION_PHRASES[action] ?? action,
  };
}

/** One field's before/after, as the diff renders it. */
export interface DiffField {
  readonly key: string;
  readonly before: string | null;
  readonly after: string | null;
  readonly changed: boolean;
}

function renderValue(value: unknown): string | null {
  if (value === undefined) return null;
  if (value === null) return "null";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function keysOf(value: unknown): string[] {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? Object.keys(value as Record<string, unknown>)
    : [];
}

/**
 * The union of both snapshots' fields, sorted, with what each held.
 *
 * **The union rather than `after`'s keys**, because a field that was removed is a change
 * and reading only the new shape hides exactly that. `before` is absent on creates and
 * `after` on deletes (§4), so a one-sided row is normal and renders as one-sided rather
 * than as an error.
 *
 * Values are compared by their **rendered** form, not by identity: these are jsonb
 * snapshots that have been through Postgres and back, so `1` and `1` are different objects
 * and the same value. That comparison is why `changed` is computed here and not in JSX.
 *
 * A non-object snapshot (a bare string, an array) has no fields to pair, so it yields a
 * single row keyed by the empty string and the caller renders it whole — which is honest
 * about a shape this function cannot decompose, rather than silently showing nothing.
 */
export function diffFields(before: unknown, after: unknown): DiffField[] {
  const beforeIsPlain =
    keysOf(before).length > 0 || before === undefined || before === null;
  const afterIsPlain = keysOf(after).length > 0 || after === undefined || after === null;

  if (!beforeIsPlain || !afterIsPlain) {
    const b = renderValue(before);
    const a = renderValue(after);
    return [{ key: "", before: b, after: a, changed: b !== a }];
  }

  /*
   * **Changed keys only** — `screens.md` §13: *"Changed keys only, never the whole
   * record … Unchanged fields are not rendered at all."*
   *
   * This returned the union of both key sets with a `changed` flag and let the table
   * decide, which put every untouched field in front of an auditor reading a diff. The
   * ruling moves the decision here, and the reason is what a diff is *for*: a row that
   * did not change is not evidence, and a screen full of them buries the two that are.
   *
   * The flag survives on the type because the single-value branch above still sets it —
   * a scalar before/after pair that happens to be equal is a real, if odd, record and
   * this function does not get to hide it.
   */
  const keys = [...new Set([...keysOf(before), ...keysOf(after)])].sort();
  return keys
    .map((key) => {
      const b = renderValue((before as Record<string, unknown> | null)?.[key]);
      const a = renderValue((after as Record<string, unknown> | null)?.[key]);
      return { key, before: b, after: a, changed: b !== a };
    })
    .filter((field) => field.changed);
}

/**
 * `2026-08-08 14:32:07` — sortable, unambiguous, UTC, and not the reader's locale.
 *
 * **Not `Intl`, and that is the correction rather than the original intent.** This shipped
 * as `Intl.DateTimeFormat("en-GB", …)` with numeric parts, on the same reasoning
 * `router.tsx`'s `today()` uses: the app UI is English while product content is
 * multi-language, so a timestamp following the browser's locale would be the one string on
 * this screen that moves between two people reading the same row.
 *
 * The locale was the right instinct and the wrong instrument. `en-GB` numeric is
 * **`DD/MM/YYYY`**, so the function produced `08/08/2026 14:32:07` while this paragraph
 * claimed `2026-08-08 14:32:07` and called it *sortable* — and `"08/08/2026" < "09/07/2026"`
 * lexicographically, which is backwards. Found by the test agent measuring the docblock's
 * own literal instead of paraphrasing it, which is the practice c34's entry recommends and
 * the second time in this commit it has caught something.
 *
 * So the format is built from the ISO representation directly. There is no locale involved
 * to be wrong about: this is a machine-shaped timestamp for a record a regulator or a
 * support conversation quotes, and `time[dateTime]` in the DOM carries the exact value
 * beside it.
 */
export function auditTimestamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  /*
   * `2026-08-08T14:32:07.123Z` → `2026-08-08 14:32`. **Minutes**, ruled by `screens.md`
   * §13 (2026-08-11): *"`WHEN` (fixed `YYYY-MM-DD HH:MM`, mono, tabular)"*.
   *
   * This rendered seconds, on the reasoning that the trail is read by people and
   * sub-second precision is never the question. The ruling agrees with the reasoning and
   * goes one unit further: minutes are what a person reads a trail in, and a column of
   * seconds is three characters of noise in every row of a fixed-width table. The
   * precision is not lost — `at` is a `timestamptz` and the API still returns it.
   */
  return at.toISOString().slice(0, 16).replace("T", " ");
}
